import {
    duckDBSignedUrlQueryOptions,
    excelSignedUrlQueryOptions,
    getProjectInfoQueryOptions,
} from '@/api/project';
import { WelcomeModal } from '@/components/WelcomeModal';
import { useAuth } from '@/config/auth';
import {
    COMPETITOR_MEDIA_INVESTMENT_ROUTE,
    MODEL_CONTRIBUTION_ROUTE,
    SPEND_SUMMARY_ROUTE,
    TITLE_DASHBOARD_ROUTE,
} from '@/config/routes';
import { DashboardGlobalProvider } from '@/dashboards/providers/dashboardGlobalContext';
import { validateDateAndPeriodSearchParams } from '@/dashboards/utils';
import {
    DB_NAME,
    DB_NAME_INPUT,
    DuckDBQueryContextProvider,
    useDuckDBConnection,
} from '@/duckdb';
import { runScripts } from '@/duckdb/scripts';
import { FEATURE_FLAG_ACTIONS, FEATURE_FLAG_SUBJECTS } from '@/features/Casl';
import { useAbilityContext } from '@/features/Casl/hooks';
import { useWelcomeModal } from '@/hooks/useWelcomeModal';
import {
    initOptimizer,
    prewarmOptimizerRuntime,
} from '@/optimizer';
import { useInitOptimizerServiceManager } from '@/optimizer/hooks/useInitOptimizerServiceManager';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { Route } from '@/routes/_org/project/$projectId/_dashboards';
import {
    FileConnectionEnum,
    type DuckDBConnectionConfig,
} from '@analytical-alley/duckdb-react';
import { FullPageLoader } from '@analytical-alley/ui';
import { useQuery } from '@tanstack/react-query';
import {
    Outlet,
    useMatches,
    useNavigate,
    useSearch,
} from '@tanstack/react-router';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';

const ROUTES_WITH_SEARCH_VALIDATION: string[] = [
    TITLE_DASHBOARD_ROUTE,
    MODEL_CONTRIBUTION_ROUTE,
    SPEND_SUMMARY_ROUTE,
    COMPETITOR_MEDIA_INVESTMENT_ROUTE,
];

export const DashboardsLayout = () => {
    const initPyOptimizerServiceManager = useInitOptimizerServiceManager();
    const { setRules, setOptimizerInitError } = useOptimizer();
    const { updateAbility, abilityInitialized, ability } = useAbilityContext();
    const { projectId } = Route.useParams();
    const { dashboardId } = Route.useSearch();
    const navigate = useNavigate();
    const { user } = useAuth();

    const isPreview = !!dashboardId;

    const { shouldShowWelcome, markWelcomeAsSeen, showWelcomeModal } =
        useWelcomeModal(projectId);

    const matches = useMatches();
    const shouldValidateSearch = useMemo(
        () =>
            matches.some((elem) =>
                ROUTES_WITH_SEARCH_VALIDATION.includes(elem.routeId),
            ),
        [matches],
    );

    const search = useSearch({ strict: false });

    const {
        data: projectData,
        isFetching,
        isLoading,
    } = useQuery(
        getProjectInfoQueryOptions({
            dashboardId,
            projectId,
        }),
    );

    const startDate = projectData?.dashboard.dataPeriodMinDate
        ? moment
              .utc(projectData.dashboard.dataPeriodMinDate)
              .format('DD-MM-YYYY')
        : '';
    const endDate = projectData?.dashboard.dataPeriodMaxDate
        ? moment
              .utc(projectData.dashboard.dataPeriodMaxDate)
              .format('DD-MM-YYYY')
        : '';

    useEffect(() => {
        if (!shouldValidateSearch || !projectData) {
            return;
        }
        const { newSearch, shouldRedirect } = validateDateAndPeriodSearchParams(
            {
                search,
                project: {
                    period: projectData.project.interval,
                    startDate,
                    endDate,
                },
            },
        );

        if (shouldRedirect) {
            navigate({
                search: newSearch,
                replace: true,
            });
        }
    }, [
        endDate,
        shouldValidateSearch,
        navigate,
        projectData,
        search,
        startDate,
    ]);

    const { data: urlData, isLoading: isDbUrlLoading } = useQuery(
        duckDBSignedUrlQueryOptions({ projectId, dashboardId }),
    );

    const { data: excelUrlData, isLoading: isExcelUrlLoading } = useQuery({
        ...excelSignedUrlQueryOptions({ projectId, dashboardId }),
    });

    const shouldLoadInputData =
        abilityInitialized &&
        ability.can(
            FEATURE_FLAG_ACTIONS.VIEW,
            FEATURE_FLAG_SUBJECTS.COMPETITORS_MEDIA_DASHBOARD,
        );
    const shouldInitOpt =
        abilityInitialized &&
        (ability.can(
            FEATURE_FLAG_ACTIONS.VIEW,
            FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD,
        ) ||
            ability.can(
                FEATURE_FLAG_ACTIONS.VIEW,
                FEATURE_FLAG_SUBJECTS.BUDGET_OPTIMIZATION_DASHBOARD,
            ));

    const {
        data: urlInputData,
        isLoading: isDbUrlInputLoading,
        isError: isInputUrlError,
    } = useQuery(
        duckDBSignedUrlQueryOptions(
            {
                projectId,
                dashboardId,
                fileType: 'input',
            },
            { enabled: shouldLoadInputData, retry: false },
        ),
    );

    const buildMediaPrices = useMemo(() => {
        return projectData?.dashboard.mediaPrices?.reduce((acc, mediaPrice) => {
            return {
                ...acc,
                [mediaPrice.channel]: mediaPrice.prices,
            };
        }, {});
    }, [projectData?.dashboard.mediaPrices]);

    const buildJsonConfig = useMemo(() => {
        const configs = projectData?.dashboard.configs.reduce((acc, config) => {
            return {
                ...acc,
                [config.key]: config.value,
            };
        }, {});
        return {
            ...configs,
            MEDIA_PRICES: buildMediaPrices,
        };
    }, [buildMediaPrices, projectData?.dashboard.configs]);

    const connectionConfigs: DuckDBConnectionConfig[] = [
        {
            url: urlData?.signedUrl,
            registerWithFileBuffer: true,
            type: FileConnectionEnum.DUCKDB,
            databaseName: DB_NAME,
        },
    ];

    if (shouldLoadInputData && urlInputData?.signedUrl) {
        connectionConfigs.push({
            url: urlInputData.signedUrl,
            registerWithFileBuffer: true,
            type: FileConnectionEnum.DUCKDB,
            databaseName: DB_NAME_INPUT,
        });
    }

    const dbConnectionEnabled =
        !!urlData?.signedUrl &&
        abilityInitialized &&
        (!shouldLoadInputData || !!urlInputData?.signedUrl || isInputUrlError);

    const { useQueryHook, isLoading: isDuckDbLoading } = useDuckDBConnection(
        connectionConfigs,
        {
            onSuccess: async () => {
                await runScripts();
            },
            queryKey: 'duckdb-connection',
            throwOnError: true,
            enabled: dbConnectionEnabled,
        },
    );

    useEffect(() => {
        if (!projectData) return;

        updateAbility(projectData.dashboard.featureFlags);
    }, [projectData, updateAbility]);

    useEffect(() => {
        if (
            !shouldInitOpt ||
            !urlData?.signedUrl ||
            !excelUrlData?.signedUrl
        ) {
            return;
        }

        prewarmOptimizerRuntime()
            .then(async () => {
                try {
                    setOptimizerInitError(null);
                    await initOptimizer({
                        consoleEnabled: true,
                        duckDbPath: urlData?.signedUrl,
                        excelPath: excelUrlData?.signedUrl,
                        jsonConfig: buildJsonConfig,
                    });

                    initPyOptimizerServiceManager();
                } catch (error) {
                    const initError =
                        error instanceof Error
                            ? error
                            : new Error('Failed to initialize optimizer');
                    setOptimizerInitError(initError);
                    // eslint-disable-next-line no-console
                    console.error('Failed to initialize optimizer:', initError);
                    return;
                }

                if (projectData?.dashboard.rules) {
                    try {
                        setRules(
                            projectData.dashboard.rules.map((rule) => ({
                                channel: rule.channel,
                                type: rule.type,
                                target: rule.target,
                                subtype: rule.subtype,
                                value: rule.value,
                            })),
                        );
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(
                            'Failed to initialize optimizer configured rules:',
                            error,
                        );
                    }
                }
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.warn('Failed to prewarm optimizer runtime:', error);
            });
    }, [
        buildJsonConfig,
        excelUrlData?.signedUrl,
        initPyOptimizerServiceManager,
        projectData?.dashboard.rules,
        setOptimizerInitError,
        setRules,
        shouldInitOpt,
        urlData?.signedUrl,
    ]);

    if (
        isFetching ||
        isLoading ||
        isDuckDbLoading ||
        isDbUrlLoading ||
        isExcelUrlLoading ||
        isDbUrlInputLoading
    ) {
        return <FullPageLoader />;
    }

    if (!abilityInitialized) {
        return <div>Couldn&apos;t read permissions!</div>;
    }

    if (!projectData) {
        return <div>Couldn&apos;t read project data!</div>;
    }

    return (
        <DuckDBQueryContextProvider useQueryHook={useQueryHook}>
            <Helmet>
                <title>
                    {isPreview ? `${projectData.dashboard.name} | ` : ''}
                    {projectData.project.name} | Analytical Alley
                </title>
            </Helmet>
            <DashboardGlobalProvider
                project={projectData.project}
                dashboard={projectData.dashboard}
                endDate={endDate}
                startDate={startDate}
                disabledChannelsRules={[]}
                contributionFormatter={(): string => {
                    throw new Error('Function not implemented.');
                }}
                showWelcomeModal={showWelcomeModal}
                isInputDuckDBAvailable={
                    shouldLoadInputData && !!urlInputData?.signedUrl
                }
            >
                <Outlet />
                {shouldShowWelcome && (
                    <WelcomeModal
                        showModal={shouldShowWelcome}
                        onClose={markWelcomeAsSeen}
                        userName={user?.profile?.name}
                    />
                )}
            </DashboardGlobalProvider>
        </DuckDBQueryContextProvider>
    );
};
